import { apiClient, isApiError } from './apiClient';

interface AlertCondition {
  id: number;
  key?: string;
  title: string;
  registered_at: string;
  prefecture: string;
  district: string;
  area_m2_min: number | null;
  area_m2_max: number | null;
  walk_minutes_min: number | null;
  walk_minutes_max: number | null;
  price_per_tsubo_min: number | null;
  price_per_tsubo_max: number | null;
  built_year_min: number | null;
  built_year_max: number | null;
  is_active: boolean;
  last_notified_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AlertConditionListResponse {
  data: AlertCondition[];
}

interface AlertConditionSingleResponse {
  data: AlertCondition;
}

interface ValidationErrorResponse {
  errors: Record<string, string[]>;
}

/**
 * 通知条件一覧を取得
 */
export async function getList(): Promise<AlertCondition[]> {
  const result = await apiClient.get<AlertConditionListResponse>('/property-alert-conditions');
  return Array.isArray(result.data) ? result.data : [];
}

/**
 * 通知条件の詳細を取得
 */
export async function getById(id: number): Promise<AlertCondition> {
  const result = await apiClient.get<AlertConditionSingleResponse>(`/property-alert-conditions/${id}`);
  return result.data;
}

/**
 * 通知条件を作成
 * @returns 作成された条件。バリデーションエラーの場合は {validationErrors} を返す
 */
export async function create(data: Record<string, unknown>): Promise<{ data?: AlertCondition; validationErrors?: Record<string, string[]> }> {
  try {
    const result = await apiClient.post<AlertConditionSingleResponse>('/property-alert-conditions', data);
    return { data: result.data };
  } catch (error) {
    if (isApiError(error) && error.status === 422 && error.data) {
      return { validationErrors: (error.data as unknown as ValidationErrorResponse).errors };
    }
    throw error;
  }
}

/**
 * 通知条件を更新
 */
export async function update(id: number, data: Record<string, unknown>): Promise<{ data?: AlertCondition; validationErrors?: Record<string, string[]> }> {
  try {
    const result = await apiClient.put<AlertConditionSingleResponse>(`/property-alert-conditions/${id}`, data);
    return { data: result.data };
  } catch (error) {
    if (isApiError(error) && error.status === 422 && error.data) {
      return { validationErrors: (error.data as unknown as ValidationErrorResponse).errors };
    }
    throw error;
  }
}

/**
 * 通知条件を削除
 */
export async function del(id: number): Promise<void> {
  return apiClient.del(`/property-alert-conditions/${id}`);
}

/**
 * アクティブ状態の切り替え
 */
export async function toggleActive(id: number): Promise<AlertCondition> {
  return apiClient.post<AlertCondition>(`/property-alert-conditions/${id}/toggle-active`, {});
}
